import { Button, Form, Table } from "antd";
import { useEffect, useState } from "react";
import { SaveOutlined } from '@ant-design/icons';
import { IEquipmentProblem, IEquipmentProblemProps, IEquipmentProblemResult, IEquipmentProblemStates } from "./interfaces";
import { Core } from "../../components/infrastructure/core";
import { useContext } from "react";
import AppContext from "../../components/infrastructure/appContext";
import { enmEntityState, enmValidationLevel, ICallOptions, IResult, IResultSet } from "../../components/infrastructure/models";
import { Redirect } from "react-router-dom";
import './index.css';
import { IProblem } from "../problem/interfaces";

type LayoutType = Parameters<typeof Form>[0]['layout'];

const EquipmentProblem = (props: IEquipmentProblemProps) => {
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState<IEquipmentProblemStates>({
        loadData: true,
        isAuthorized: true,
        selectedItems: [],
        equipmentProblems: [],
        problems: [],
        saveAccess: false,
        currentEquipmentId: props.params().equipment?.Id
    });
    const { loadData } = state;
    const [form] = Form.useForm();
    const [formLayout] = useState<LayoutType>('horizontal');
    const formItemLayout = {
        labelCol: { span: 4 },
        wrapperCol: { span: 18 },
    };

    useEffect(() => {
        if (props.params().equipment.Id !== state.currentEquipmentId) {
            setState({
                loadData: true,
                isAuthorized: true,
                selectedItems: [],
                equipmentProblems: [],
                problems: [],
                saveAccess: false,
                currentEquipmentId: props.params().equipment.Id
            });
            setLoading(true);
        }
    }, [props]);

    if (!state.isAuthorized) {
        let loginUrl = '/login';
        if (context?.path)
            loginUrl += '?' + encodeURIComponent(context.path);
        return (<Redirect to={loginUrl} />);
    }

    if (loadData) {
        let res: IResultSet<IEquipmentProblemResult> | null = null;

        let opt: ICallOptions = {
            url: "EquipmentProblem/getequipments",
            data: { equipmentId: props.params().equipment.Id},
            onSuccess: (result) => {
                res = result as any;
            },
            onComplete: () => {
                if (res && res.Data) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            equipmentProblems: [],
                            problems: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }

                    let selectedItems = res.Data.EquipmentProblems.map(e => {
                        return res!.Data!.Problems.find(d => d.Id === e.ProblemId)?.RowGuid!;
                    });

                    setState({
                        loadData: false,
                        isAuthorized: true,
                        equipmentProblems: res.Data.EquipmentProblems,
                        selectedItems: selectedItems,
                        problems: res.Data.Problems,
                        saveAccess: res.Data.SaveAccess,
                        currentEquipmentId: props.params().equipment?.Id
                    });
                    setLoading(false);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    equipmentProblems: [],
                    selectedItems: [],
                    problems: [],
                    saveAccess: false,
                    currentEquipmentId: props.params().equipment?.Id
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    }

    const save = (changes: IEquipmentProblem[]) => {
        if (!changes.length) {
            Core.warningNotification("لطفا ابتدا اطلاعات را تغییر دهید");
            return;
        }
        setLoading(true);
        let res: IResult | null = null;

        let opt: ICallOptions = {
            url: "EquipmentProblem/save",
            data: { entities: changes },
            onSuccess: (result) => {
                res = result;
            },
            onComplete: () => {
                if (res) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            equipmentProblems: [],
                            problems: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }
                    Core.successNotification("اطلاعات با موفقیت ذخیره شد");
                    setState({
                        loadData: true,
                        isAuthorized: true,
                        equipmentProblems: [],
                        selectedItems: [],
                        problems: [],
                        saveAccess: false,
                        currentEquipmentId: state.currentEquipmentId
                    });
                    setLoading(true);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    equipmentProblems: [],
                    selectedItems: [],
                    problems: [],
                    saveAccess: false,
                    currentEquipmentId: state.currentEquipmentId
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    };

    return (
        <Form
            {...formItemLayout}
            layout={formLayout}
            form={form}
            initialValues={{ layout: formLayout }}
            onFinish={() => {
                let changes: IEquipmentProblem[] = [];
                state.selectedItems.forEach(item => {
                    let problem = state.problems.find(d => d.RowGuid === item);
                    if (problem && !state.equipmentProblems.find(r => r.ProblemId === problem?.Id)) {
                        changes.push({ RowGuid: Core.newId(), ProblemId: problem.Id, EquipmentId: state.currentEquipmentId!, State: enmEntityState.Added } as any);
                    }
                });
                state.equipmentProblems.forEach(item => {
                    let problem = state.problems.find(d => d.Id === item.ProblemId);
                    if (problem && !state.selectedItems.find(r => r === problem?.RowGuid)) {
                        changes.push({ Id: item.Id, RowGuid: item.RowGuid, RowVrsn: item.RowVrsn, State: enmEntityState.Deleted } as any);
                    }
                });

                save(changes);
            }}
            style={{ margin: 10 }}
        >
            
            <Form.Item
                label={"مشکلات"}
                name={"Problem"}
                key={"Problem"}
                style={{ margin: 10 }}
            >
                <Table
                    rowKey="RowGuid"
                    onHeaderRow={() => {
                        return {
                            className: 'ant-table-thead-checkbox',
                        };
                    }}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: state.selectedItems,
                        onChange: (selectedRowKeys: React.Key[], _: IProblem[]) => {
                            setState({
                                loadData: false,
                                isAuthorized: true,
                                equipmentProblems: state.equipmentProblems,
                                selectedItems: selectedRowKeys,
                                problems: state.problems,
                                saveAccess: state.saveAccess,
                                currentEquipmentId: state.currentEquipmentId
                            });
                            setLoading(false);
                        },
                    }}
                    pagination={{
                        style: { marginLeft: 10 }
                    }}
                    dataSource={state.problems}
                    className="box"
                    columns={[
                        {
                            dataIndex: "Name",
                            title: "عنوان"
                        }
                    ]}
                    loading={loading}
                />
            </Form.Item>

            <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ borderTop: '1px solid', borderColor: '#e5e5e5', marginLeft: 20 }}>
                <Button disabled={!state.saveAccess} type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10 }}>
                    ذخیره
                </Button>
            </Form.Item>
        </Form>
    );
}

export default EquipmentProblem;